/**
 * DataStatusBanner — fixed top banner shown while data is paused or stalled.
 *   • SERVER (red):  Binance rate-limited our server (418/429). All market data
 *     is paused for everyone until the cooldown clears.
 *   • USER (amber):  THIS browser hit our per-IP request limit (429). Only this
 *     client is throttled; it clears on its own.
 *   • STALE (amber): the WS bridge went silent — prices on screen are frozen.
 *     Better an honest "данные задерживаются с HH:MM" than a quietly dead UI.
 * Renders nothing when nothing is wrong.
 */
import { useEffect, useState } from 'react';
import { subscribe, startDataGate } from '../utils/dataGate';
import { getLastDataTs } from '../hooks/useWebSocket';
import { t, useI18n } from '../hooks/useI18n';
import { cn } from '../ui/cn';

// How long the bridge may stay silent before we call the pipeline stalled.
// Normal cadence is a global_update every ~2s + tickers, so 45s = clearly dead.
const STALE_AFTER_MS = 45_000;

// The per-browser USER throttle is a personal, transient event — show it for
// just 5s, then let it vanish (the 30s request back-off keeps working silently).
// Server-wide cooldown and pipeline staleness stay persistent: those are health
// problems the user needs to keep seeing.
const USER_NOTICE_MS = 5_000;

export default function DataStatusBanner() {
  useI18n(); // re-render on language change
  const [s, setS] = useState(null);
  const [, force] = useState(0);
  const [userNoticeUntil, setUserNoticeUntil] = useState(0);

  useEffect(() => {
    startDataGate();
    let wasThrottled = false;
    const off = subscribe((next) => {
      setS(next);
      // Rising edge of this browser's throttle → open a 5s notice window.
      if (next?.userThrottled && !wasThrottled) setUserNoticeUntil(Date.now() + USER_NOTICE_MS);
      wasThrottled = !!next?.userThrottled;
    });
    // Tick every second so the countdown / staleness updates.
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => { off(); clearInterval(t); };
  }, []);

  const lastData = getLastDataTs();
  const stale = lastData > 0 && Date.now() - lastData > STALE_AFTER_MS;

  // USER throttle only counts as "showable" during its 5s notice window.
  const userNotice = s?.userThrottled && Date.now() < userNoticeUntil;
  const rateLimited = s && (s.serverCooldown || userNotice);
  if (!rateLimited && !stale) return null;

  const server = s?.serverCooldown;
  const remMs = server ? s.serverRemainingMs : s?.userRemainingMs;
  const secs = remMs ? Math.ceil(remMs / 1000) : 0;

  let text;
  if (server) {
    text = t('banner.server').replace('{s}', secs ? ` (~${secs}s)` : '');
  } else if (userNotice) {
    text = t('banner.user').replace('{s}', secs ? ` (~${secs}s)` : '');
  } else {
    const since = new Date(lastData).toLocaleTimeString('en-GB', { hour12: false });
    text = t('banner.stale').replace('{t}', since);
  }

  // Мигрирован на Tailwind по образцу Navigation: утилиты вместо inline-стилей,
  // conditional-классы через cn. Точные фирменные оттенки баннера сохранены
  // arbitrary-значениями; амбер-старт = токен warn (#FFB84D).
  return (
    <div
      className={cn(
        'fixed inset-x-0 top-0 z-[9999] flex items-center justify-center gap-2.5',
        'px-4 py-[9px] font-mono text-[12.5px] font-semibold tracking-[0.2px] text-[#0b0e16]',
        'shadow-[0_4px_18px_rgba(0,0,0,0.35)]',
        server
          ? 'bg-linear-to-r from-[#ff6b7a] to-[#ff8a7a]'
          : 'bg-linear-to-r from-warn to-[#e8d39a]'
      )}
    >
      <span
        className={cn(
          'h-2 w-2 flex-none rounded-full bg-[#0b0e16]',
          server ? 'shadow-[0_0_8px_#ff6b7a]' : 'shadow-[0_0_8px_#FFB84D]'
        )}
      />
      <span>{text}</span>
    </div>
  );
}
